import * as Dialog from "@radix-ui/react-dialog";
import { Suspense, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import ExplainDrillPanel from "./explain-drill-panel";
import type { CalcDrill } from "./explain-drill-panel";
import type {
  explainPanel_Query,
  MetricExplanationEnum,
  CohortDimensionEnum,
} from "@app/queries/explainPanel_Query.graphql";

// A node mirrors Types::CalcNode. The Relay query selects a fixed depth; this
// recursive shape lets one component render every tier. `children` is optional
// because the deepest selected tier omits it (its nodes are leaves).
type RawNode = {
  readonly key: string;
  readonly label: string;
  readonly formula: string | null;
  readonly value: number | null;
  readonly unit: string;
  readonly source: string;
  readonly note: string | null;
  readonly drill: CalcDrill | null;
  readonly children?: ReadonlyArray<RawNode>;
};

// Fields are inlined (not a shared fragment) at each of the three tiers so the data
// is read directly off the query result — Relay masks fragment data, and these drill
// panels read fields directly (mirroring pnl-cell-panel). Three tiers cover the
// deepest tree (Rule of 40 → margin → TTM expenses; LTV → gross margin → revenue).
const explainQuery = graphql`
  query explainPanel_Query(
    $metric: MetricExplanationEnum!
    $month: String
    $dimension: CohortDimensionEnum
    $value: String
  ) {
    metricExplanation(metric: $metric, month: $month, dimension: $dimension, value: $value) {
      key
      label
      formula
      value
      unit
      source
      note
      drill { pnlLine flow excludeDistributions classification categoryId from to }
      children {
        key
        label
        formula
        value
        unit
        source
        note
        drill { pnlLine flow excludeDistributions classification categoryId from to }
        children {
          key
          label
          formula
          value
          unit
          source
          note
          drill { pnlLine flow excludeDistributions classification categoryId from to }
        }
      }
    }
  }
`;

function formatValue(value: number | null, unit: string): string {
  if (value == null) return "—";
  switch (unit) {
    case "CENTS":
      return (value / 100).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      });
    case "RATIO":
      return `${(value * 100).toFixed(1)}%`;
    case "POINTS":
      return value.toFixed(0);
    case "COUNT":
      return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
    default:
      return String(value);
  }
}

const SOURCE_STYLE: Record<string, string> = {
  BANK: "bg-emerald-50 text-emerald-700",
  STRIPE: "bg-indigo-50 text-indigo-700",
  DERIVED: "bg-gray-100 text-gray-500",
};

const SOURCE_LABEL: Record<string, string> = {
  BANK: "bank transactions",
  STRIPE: "Stripe",
  DERIVED: "computed",
};

export type ExplainMetric = MetricExplanationEnum;
export type CohortDimension = CohortDimensionEnum;

// The disclosed-calculation panel: a KPI's formula, its inputs, and a one-click
// drill into the exact transactions behind every bank-derived input. For a retention
// metric, an optional segment (dimension + value) scopes the cohort to one /explore slice.
export default function ExplainPanel({
  metric,
  month,
  dimension,
  value,
  title,
  onClose,
}: {
  metric: ExplainMetric;
  month?: string | null;
  dimension?: CohortDimension | null;
  value?: string | null;
  title: string;
  onClose: () => void;
}) {
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/30 z-40" />
        <Dialog.Content className="fixed right-0 top-0 h-full w-[560px] max-w-[97vw] bg-white shadow-2xl z-50 flex flex-col focus:outline-none">
          <div className="flex items-start justify-between px-5 py-4 border-b shrink-0">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                Explain this number
              </p>
              <h2 className="font-bold text-gray-900">{title}</h2>
            </div>
            <button type="button" onClick={onClose} className="text-sm text-gray-500 hover:text-gray-900">
              Close
            </button>
          </div>
          <Suspense fallback={<div className="p-6 text-sm text-gray-400">Loading…</div>}>
            <ExplainContent metric={metric} month={month ?? null} dimension={dimension ?? null} value={value ?? null} />
          </Suspense>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ExplainContent({
  metric,
  month,
  dimension,
  value,
}: {
  metric: ExplainMetric;
  month: string | null;
  dimension: CohortDimension | null;
  value: string | null;
}) {
  const data = useLazyLoadQuery<explainPanel_Query>(
    explainQuery,
    { metric, month, dimension, value },
    { fetchPolicy: "store-or-network" }
  );
  const [drill, setDrill] = useState<{ title: string; drill: CalcDrill } | null>(null);
  const root = data.metricExplanation as unknown as RawNode;

  return (
    <>
      <div className="overflow-auto flex-1 px-5 py-4">
        <NodeRow node={root} depth={0} onDrill={(title, d) => setDrill({ title, drill: d })} />
        <p className="mt-6 text-[11px] leading-relaxed text-gray-400">
          Every <span className="text-emerald-700 font-medium">bank-transactions</span> input drills to exactly
          the transactions that sum to it. <span className="text-indigo-700 font-medium">Stripe</span> inputs come
          from subscription data — there are no bank transactions behind them.
        </p>
      </div>
      {drill && (
        <ExplainDrillPanel title={drill.title} drill={drill.drill} onClose={() => setDrill(null)} />
      )}
    </>
  );
}

function NodeRow({
  node,
  depth,
  onDrill,
}: {
  node: RawNode;
  depth: number;
  onDrill: (title: string, drill: CalcDrill) => void;
}) {
  const children = node.children ?? [];
  return (
    <div className={depth === 0 ? "" : "mt-3"}>
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <span className={depth === 0 ? "font-bold text-gray-900" : "font-medium text-gray-800"}>
            {node.label}
          </span>
          <span
            className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-medium align-middle ${
              SOURCE_STYLE[node.source] ?? "bg-gray-100 text-gray-500"
            }`}
          >
            {SOURCE_LABEL[node.source] ?? node.source.toLowerCase()}
          </span>
        </div>
        <span
          className={`shrink-0 tabular-nums ${
            depth === 0 ? "text-lg font-bold text-gray-900" : "font-semibold text-gray-700"
          }`}
        >
          {formatValue(node.value, node.unit)}
        </span>
      </div>

      {node.formula && <p className="mt-0.5 text-xs text-gray-500">{node.formula}</p>}
      {node.note && <p className="mt-0.5 text-[11px] text-gray-400">{node.note}</p>}

      {node.drill && (
        <button
          type="button"
          onClick={() => onDrill(node.label, node.drill as CalcDrill)}
          className="mt-1 text-xs font-medium text-emerald-700 hover:text-emerald-900 hover:underline"
        >
          View the transactions behind this →
        </button>
      )}

      {children.length > 0 && (
        <div className="mt-2 border-l-2 border-gray-100 pl-4">
          {children.map((child) => (
            <NodeRow key={child.key} node={child} depth={depth + 1} onDrill={onDrill} />
          ))}
        </div>
      )}
    </div>
  );
}
